import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { SettingsService } from '../api/services/setting.service';
import { environment } from '../../environments/environment';
import { TTSService } from './tts.service';

@Injectable({
  providedIn: 'root'
})
export class AudioService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private currentMapAudio: HTMLAudioElement | null = null;
  private elementAudioMap = new Map<string, HTMLAudioElement>();
  private applicationSoundVolume$ = new BehaviorSubject<number>(1);
  private currentlyFocusedElementId: string | null = null;

  constructor(private settingsService: SettingsService, private ttsService: TTSService) {
    // Subscribe to settings changes to update application sound volume
    this.settingsService.generalInfoChanged
      .pipe(takeUntil(this.destroy$))
      .subscribe((generalInfo) => {
        if (generalInfo?.soundVolume !== undefined) {
          this.applicationSoundVolume$.next(generalInfo.soundVolume);
          this.updateAllAudioVolumes();
        }
      });

    // Initialize with current settings (only need once)
    this.settingsService.getGeneralInfo()
      .pipe(take(1))
      .subscribe((generalInfo) => {
        if (generalInfo?.soundVolume !== undefined) {
          this.applicationSoundVolume$.next(generalInfo.soundVolume);
        }
      });
  }

  /**
   * Play background sound for a map in continuous loop
   */
  playMapBackgroundSound(soundUrl: string, mapSoundVolume: number): void {
    this.stopMapBackgroundSound();

    if (!soundUrl || !this.isValidAudioUrl(soundUrl)) {
      return;
    }
    try {
      const proxiedUrl = this.getProxiedAudioUrl(soundUrl);
      this.currentMapAudio = new Audio(proxiedUrl);
      this.currentMapAudio.loop = true;
      this.currentMapAudio.volume = this.calculateMapSoundVolume(mapSoundVolume);
      // Store original volume for recalculation when app volume changes
      this.currentMapAudio.dataset['originalVolume'] = mapSoundVolume.toString();

      this.currentMapAudio.addEventListener('canplay', () => {
        // Only play map sound if no cy elements are focused
        if (!this.isAnyElementAudioPlaying()) {
          this.currentMapAudio?.play().catch(error => {
            console.warn('Failed to play map background sound:', error);
          });
        }
      });

      this.currentMapAudio.addEventListener('error', (error) => {
        console.warn('Error loading map background sound:', error);
      });

      this.currentMapAudio.load();
    } catch (error) {
      console.warn('Failed to create map background audio:', error);
    }
  }

  /**
   * Stop the current map background sound
   */
  stopMapBackgroundSound(): void {
    if (this.currentMapAudio) {
      this.currentMapAudio.pause();
      this.currentMapAudio.currentTime = 0;
      this.currentMapAudio = null;
    } else {
      if (typeof (window as any).stopAllSounds === 'function') {
        (window as any).stopAllSounds();
      }
    }
  }

  /**
   * Play sound effect for an element (node or link) once - PRIVATE, use handleElementFocusChange() instead
   */
  private playElementSound(elementId: string, soundUrl: string): void {
    if (!soundUrl || !this.isValidAudioUrl(soundUrl)) {
      return;
    }

    // Stop any existing sound for this element
    this.stopElementSound(elementId);

    try {
      const proxiedUrl = this.getProxiedAudioUrl(soundUrl);
      const audio = new Audio(proxiedUrl);
      audio.volume = this.calculateElementSoundVolume();

      // DON'T auto-remove from map when playback ends - we'll manage this manually
      audio.addEventListener('ended', () => {
        // Just clear focused element tracking, but keep in map so we can stop it if needed
        if (this.currentlyFocusedElementId === elementId) {
          this.currentlyFocusedElementId = null;
        }
      });

      audio.addEventListener('error', (error) => {
        console.warn('Error loading element sound:', error);
        // On error, we can remove it since it's not playing
        this.elementAudioMap.delete(elementId);
        if (this.currentlyFocusedElementId === elementId) {
          this.currentlyFocusedElementId = null;
        }
      });

      this.elementAudioMap.set(elementId, audio);

      audio.play().catch(error => {
        console.warn('Failed to play element sound:', error);
        this.elementAudioMap.delete(elementId);
        if (this.currentlyFocusedElementId === elementId) {
          this.currentlyFocusedElementId = null;
        }
      });
    } catch (error) {
      console.warn('Failed to create element audio:', error);
    }
  }

  isPlayingMapSound(): boolean {
    return this.currentMapAudio !== null && !this.currentMapAudio.paused;
  }

  /**
   * Stop sound effect for a specific element
   */
  stopElementSound(elementId: string): void {
    const audio = this.elementAudioMap.get(elementId);
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      this.elementAudioMap.delete(elementId);
      // Clear focused element tracking if this was the focused element
      if (this.currentlyFocusedElementId === elementId) {
        this.currentlyFocusedElementId = null;
      }
    }
  }

  /**
   * Update map background sound volume when settings change
   */
  updateMapSoundVolume(mapSoundVolume: number): void {
    if (this.currentMapAudio) {
      this.currentMapAudio.volume = this.calculateMapSoundVolume(mapSoundVolume);
      // Update stored original volume
      this.currentMapAudio.dataset['originalVolume'] = mapSoundVolume.toString();
    }
  }

  /**
   * Check if map background audio is currently playing
   */
  isMapAudioPlaying(): boolean {
    return !!(this.currentMapAudio && !this.currentMapAudio.paused);
  }

  isAnyElementAudioPlaying(): boolean {
    return Array.from(this.elementAudioMap.values())
      .some(audio => !audio.paused);
  }

  /**
   * Stop ALL element audio using the global monkey-patched stopAllSounds function
   */
  private stopAllElementAudio(): void {
    // Use the global stopAllSounds function from main.ts monkey patch
    if (typeof (window as any).stopAllSounds === 'function') {
      (window as any).stopAllSounds();
    }

    // Clear our internal map since all sounds are stopped
    this.elementAudioMap.clear();
  }

  /**
   * Handle element focus change - stops any previous element sounds and plays new element sound if available
   */
  handleElementFocusChange(element: any): void {
    // Only play sound if element has its own soundUrl
    if (element) {
      const elementId = element.id || '';
      const soundUrl = element.properties?.soundUrl || element.soundUrl;

      this.currentlyFocusedElementId = elementId;

      if (soundUrl) {
        //  this.stopAllElementAudio();
        this.playElementSound(elementId, soundUrl);
      }
      // NO DEFAULT SOUND - if no soundUrl, play nothing
    } else {
      this.currentlyFocusedElementId = null;
    }
  }

  /**
   * Handle element focus cleared (when tappedNode/tappedLink becomes null)
   */
  handleElementFocusCleared(): void {
    // Always stop TTS audio when elements are deselected
    this.ttsService.stop();

    if (!this.currentlyFocusedElementId) {
      // is a map audio playing?
      if (!this.currentMapAudio?.paused) {
        // If map audio is playing, we don't stop it
        return;
      }
    } else {
      // if we are switching from one element that has no sound to canvas that had sound, we do not stop the map audio
      if (this.currentMapAudio && !this.currentMapAudio.paused) {
        return;
      }
    }
    // Stop ALL currently playing element audio
    this.stopAllElementAudio();
    this.currentlyFocusedElementId = null;
  }

  /**
   * Check if URL is a valid audio file type
   */
  private isValidAudioUrl(url: string): boolean {
    const audioExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac', '.wma'];
    return audioExtensions.some(ext =>
      url.toLowerCase().includes(ext) || url.toLowerCase().endsWith(ext)
    );
  }

  /**
   * Calculate final volume for map background sound
   * Formula: SSV * ASV (Map Sound Volume * App Sound Volume)
   * Note: OS Volume is handled by the browser/system and cannot be accessed via JavaScript
   */
  private calculateMapSoundVolume(mapSoundVolume: number): number {
    const appVolume = this.applicationSoundVolume$.value; // ASV (Global Volume from settings)
    return Math.max(0, Math.min(1, mapSoundVolume * appVolume));
  }

  /**
   * Calculate final volume for element sound effects
   * Formula: ASV (App Sound Volume)
   * Note: OS Volume is handled by the browser/system and cannot be accessed via JavaScript
   */
  private calculateElementSoundVolume(): number {
    const appVolume = this.applicationSoundVolume$.value; // ASV (Global Volume from settings)
    return Math.max(0, Math.min(1, appVolume));
  }

  /**
   * Update volume for all currently playing audio
   */
  private updateAllAudioVolumes(): void {
    // Update map background sound volume if playing
    if (this.currentMapAudio) {
      // We need the original map sound volume to recalculate
      // For now, we'll assume it's stored in the audio element's dataset
      const mapVolume = parseFloat(this.currentMapAudio.dataset['originalVolume'] || '0.5');
      this.currentMapAudio.volume = this.calculateMapSoundVolume(mapVolume);
    }

    // Update element sound volumes
    this.elementAudioMap.forEach((audio) => {
      audio.volume = this.calculateElementSoundVolume();
    });
  }

  /**
   * Get current application sound volume as observable
   */
  getApplicationSoundVolume() {
    return this.applicationSoundVolume$.asObservable();
  }

  /**
   * Check if any audio is currently playing
   */
  isAnyAudioPlaying(): boolean {
    const mapAudioPlaying = this.currentMapAudio && !this.currentMapAudio.paused;
    const elementAudioPlaying = Array.from(this.elementAudioMap.values())
      .some(audio => !audio.paused);

    return mapAudioPlaying || elementAudioPlaying;
  }

  /**
   * Mute all audio (for user preference to turn off sounds)
   */
  muteAll(): void {
    if (this.currentMapAudio) {
      this.currentMapAudio.muted = true;
    }
    this.elementAudioMap.forEach(audio => {
      audio.muted = true;
    });
  }

  /**
   * Unmute all audio
   */
  unmuteAll(): void {
    if (this.currentMapAudio) {
      this.currentMapAudio.muted = false;
    }
    this.elementAudioMap.forEach(audio => {
      audio.muted = false;
    });
  }

  /**
   * Get proxied audio URL to avoid CORS issues with external audio files
   */
  private getProxiedAudioUrl(soundUrl: string): string {
    // Check if it's already a proxied URL or a relative URL
    if (soundUrl.includes('/api/public/audio') || !soundUrl.startsWith('http')) {
      return soundUrl;
    }

    // Use the audio proxy endpoint for external URLs
    return `${environment.apiUrl}/public/audio?url=${encodeURIComponent(soundUrl)}`;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Clean up audio resources
    this.stopMapBackgroundSound();
    this.stopAllElementAudio();
  }
}
